import React from 'react';
import { useTranslation } from 'react-i18next';

import { SpinnerInline } from '../../../components/spinner';
import { numberSeparator } from '../../../helpers/formats';
import { getPercentageFromFraction } from '../../../utils/finance';

interface DetailsProps {
  id: string;
  downPayment?: number;
  duration?: number;
  residual?: number;
  interest: number;
  effectiveInterest: number;
  setupFee: number;
  registrationFee?: number | null;
  administrationFee: number;
  totalCreditCost: number;
  monthlyCost: number;
  financialInstitutionCtaUrl: string;
  financialInstitutionCtaName: string;
  financialInstitution: string;
  isPending: boolean;
  vehiclePrice: number;
}

const Details = ({
  id,
  downPayment,
  duration,
  residual,
  interest,
  effectiveInterest,
  setupFee,
  registrationFee,
  administrationFee,
  totalCreditCost,
  monthlyCost,
  financialInstitutionCtaUrl,
  financialInstitutionCtaName,
  financialInstitution,
  isPending,
  vehiclePrice,
}: DetailsProps) => {
  const { t } = useTranslation(['common', 'finance', 'currency']);

  const invalidCombination = monthlyCost === 0;

  const formattedInterest = getPercentageFromFraction(interest);
  const formattedEffectiveInterest = getPercentageFromFraction(effectiveInterest);
  const residualFormatted = getPercentageFromFraction(residual ?? 0);

  const getFormattedDownPaymentPercentage = () => {
    if (!downPayment) {
      return undefined;
    }

    const percentage = (downPayment / vehiclePrice) * 100;
    return Number.isInteger(percentage) ? percentage.toFixed(0) : percentage.toFixed(2);
  };

  const formattedDownPaymentPercentage = getFormattedDownPaymentPercentage();

  return (
    <div className="modal-banner">
      <div data-wx-extendbox="" className={invalidCombination ? '' : 'is-open'}>
        <div className="extend-box-header">
          <div data-am-columnrow="always">
            <div className="column">{t('monthlyCost', { ns: 'finance' })}</div>
            <div className="column">
              {!isPending && (
                <div className="h5 m-b-mini">
                  {t('monthlyCost', {
                    ns: 'currency',
                    price: monthlyCost > 0 ? numberSeparator(monthlyCost) : 0,
                  })}
                </div>
              )}
              {isPending && (
                <div className="h5 m-b-mini">
                  <SpinnerInline />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="extend-box-body font-size-small">
          {!!formattedDownPaymentPercentage && (
            <div data-am-columnrow="always">
              <div className="column">{t('downPayment', { ns: 'finance' })}</div>
              <div className="column">
                {t('currency', { ns: 'currency', price: numberSeparator(downPayment!) })} (
                {formattedDownPaymentPercentage}%)
              </div>
            </div>
          )}
          {residual !== undefined && residual > 0 && (
            <div data-am-columnrow="always" className="m-t-half">
              <div className="column">{t('residual', { ns: 'finance' })}</div>
              <div className="column">
                {t('currency', {
                  ns: 'currency',
                  price: numberSeparator(residual * vehiclePrice),
                })}{' '}
                ({residualFormatted}%)
              </div>
            </div>
          )}

          {!!duration && (
            <div data-am-columnrow="always" className="m-t-half">
              <div className="column">{t('duration', { ns: 'finance' })}</div>
              <div className="column">
                {duration} {t('monthsUnit', { ns: 'finance' })}
              </div>
            </div>
          )}

          <div data-am-columnrow="always" className="m-t-half">
            <div className="column">{t('interest', { ns: 'finance' })}</div>
            <div className="column">{formattedInterest}%</div>
          </div>
          <div data-am-columnrow="always" className="m-t-half">
            <div className="column">{t('effectiveInterest', { ns: 'finance' })}</div>
            <div className="column">{formattedEffectiveInterest}%</div>
          </div>
          <div data-am-columnrow="always" className="m-t-half">
            <div className="column">{t('setupFee', { ns: 'finance' })}</div>
            <div className="column">
              {t('currency', { ns: 'currency', price: numberSeparator(setupFee) })}
            </div>
          </div>
          {registrationFee !== undefined && registrationFee !== null && (
            <div data-am-columnrow="always" className="m-t-half">
              <div className="column">{t('registrationFee', { ns: 'finance' })}</div>
              <div className="column">
                {t('currency', { ns: 'currency', price: numberSeparator(registrationFee) })}
              </div>
            </div>
          )}
          <div data-am-columnrow="always" className="m-t-half">
            <div className="column">{t('administrationFee', { ns: 'finance' })}</div>

            <div className="column">
              {t('monthlyCost', { ns: 'currency', price: numberSeparator(administrationFee) })}
            </div>
          </div>
          <div data-am-columnrow="always" className="m-t-half">
            <div className="column">{t('totalCreditCost', { ns: 'finance' })}</div>
            <div className="column">
              {t('currency', { ns: 'currency', price: numberSeparator(totalCreditCost) })}
            </div>
          </div>

          {!!financialInstitutionCtaUrl && !!financialInstitutionCtaName && (
            <div className="m-t">
              <a
                href={financialInstitutionCtaUrl.replace('{id}', id)}
                title={
                  financialInstitution
                    ? t('goToFinancialInstitution', { ns: 'finance', financialInstitution })
                    : t('moreInformation', { ns: 'finance' })
                }
                target="_blank"
                rel="nofollow noopener noreferrer"
                data-am-link="font-inherit"
              >
                {t('moreInformation', { ns: 'finance' })}
                <i className="icon-link-external m-l-half" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Details;
